import apiClient from "./api";

/**
 * True for both custom-trigger display types. They describe the same pane —
 * mounted hidden and opened by host page code via `StrikeUpChat.open()` —
 * differing only in whether the visitor can drag it wider:
 *   custom-trigger        → fixed width
 *   custom-trigger-panel  → resizable, width remembered per visitor
 *
 * Everything that gates UI on "is this widget host-triggered" must go through
 * here. These are `===` comparisons, so a missed call site silently treats the
 * panel variant as an ordinary popup.
 */
export const isCustomTriggerDisplay = (displayType?: string): boolean =>
  displayType === "custom-trigger" || displayType === "custom-trigger-panel";

/**
 * Per-language override of a shortcut card. A blank field falls back to the base value.
 * Declared as a type alias rather than an interface so it carries an implicit index
 * signature and can be handed straight to the generic `TranslationTabs` editor.
 */
export type ShortcutCardTranslation = {
  title?: string;
  prompt?: string;
};

/** Per-language override of the welcome-screen greeting. */
export type WelcomeScreenTextTranslation = {
  text?: string;
};

/**
 * Brandable chat labels, keyed by label name (see `CHAT_LABEL_MAX_LENGTHS`). A label that is
 * absent or blank means the widget shows its built-in wording in the visitor's language.
 */
export type ChatLabels = {
  default?: Record<string, string>;
  // Language code → label name → translated text. A language may cover only some labels.
  translations?: Record<string, Record<string, string>>;
};

/** Label names the API accepts, with the longest text each allows (mirrors `ChatLabelKeys`). */
export const CHAT_LABEL_MAX_LENGTHS = {
  kbCitation: 40,
  inviteAgent: 40,
  handoffConnecting: 80,
  handoffWaiting: 80,
  handoffAgentJoined: 80,
  handoffAgentName: 40,
  handoffMessageQueued: 160,
  handoffEndChat: 40,
  handoffPlaceholder: 80,
  handoffEnded: 80,
  handoffFailed: 80,
} as const;

export interface ShortcutCard {
  id: string;
  title: string;
  prompt: string;
  order: number;
  icon?: string;
  /** Language code → translated fields. Absent means the card is single-language. */
  translations?: Record<string, ShortcutCardTranslation>;
}

export interface ShortcutCardInput {
  id?: string;
  title: string;
  prompt: string;
  order: number;
  icon?: string;
  /**
   * The API syncs cards by replace-all, so this MUST be round-tripped on every save —
   * omitting it deletes the card's stored translations.
   */
  translations?: Record<string, ShortcutCardTranslation>;
}

export interface ServerMapping {
  serverId: string;
  server?: {
    id: string;
    name: string;
  };
}

export interface CustomFontSettings {
  bodyFamily?: string;
  bodyUrl?: string;
  headingFamily?: string;
  headingUrl?: string;
}

export interface WidgetConfig {
  id?: string;
  name: string;
  description?: string;
  applicationToken?: string;
  apiUrl?: string;
  buttonPosition?: string;
  buttonColor?: string;
  buttonBackgroundColor?: string;
  buttonSize?: string;
  buttonMargin?: string;
  iframeWidth?: string;
  iframeHeight?: string;
  displayType?: string;
  displayBehaviour?: string;
  modalBackdrop?: boolean;
  svgIcon?: string;
  customHeaders?: string;
  styleOverrides?: Record<string, string> | string;
  // Custom fonts (body + optional heading family/URL). Empty = default Geist.
  customFonts?: CustomFontSettings;
  authorizationType?: string;
  staticAuthHeaders?: string;
  dynamicAuthHeaders?: string;
  allowAuthUpdate?: boolean;
  authInstructionsUrl?: string;
  includeUrls?: string;
  excludeUrls?: string;
  /** The tenant's https page that embeds this widget; notification Open links land here. Blank clears it. */
  appPageUrl?: string | null;
  aiProvider?: string;
  aiModel?: string;
  enableFeedback?: boolean;
  serverId?: string;
  serverIds?: string[]; // Multi-server support
  serverMappings?: ServerMapping[]; // Server mappings with names
  // Human handoff settings
  handoffEnabled?: boolean;
  handoffLevel?: number;
  handoffSupportIntegrationId?: string | null;
  handoffInboxId?: string | null;
  handoffInboxName?: string | null;
  // Customer authentication (P2/P3) — when true, the widget forces the PIN/Password
  // popup before mounting the chat. Server-authoritative.
  requiresEndUserAuth?: boolean;
  // Customer Portal toggles — master + 6 per-page flags.
  portalEnabled?: boolean;
  portalMembersEnabled?: boolean;
  portalKnowledgeBaseEnabled?: boolean;
  portalSkillsEnabled?: boolean;
  portalWorkflowsEnabled?: boolean;
  portalIntegrationsEnabled?: boolean;
  /** Show the Portal's Usage page — the AI allowance standing (WP-70). Default true. */
  portalUsageEnabled?: boolean;
  portalSettingsEnabled?: boolean;
  portalPromptsEnabled?: boolean;
  promptShortcutsEnabled?: boolean;
  // Knowledge Base settings
  kbDocumentsEnabled?: boolean;
  kbDocumentIds?: string[];
  kbWebSourceIds?: string[];
  kbFolderIds?: string[];
  // Co-pilot shortcut cards shown on the welcome screen
  shortcutCards?: ShortcutCard[];
  // Optional welcome message displayed above the shortcut cards
  welcomeScreenText?: string;
  // Language code → translated welcome text. Absent means one language only.
  welcomeScreenTextTranslations?: Record<string, WelcomeScreenTextTranslation>;
  // "Verified" badge, invite-agent buttons, … Absent label = built-in wording.
  chatLabels?: ChatLabels;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateWidgetRequest {
  name: string;
  description?: string;
  apiUrl?: string;
  buttonPosition?: string;
  buttonColor?: string;
  buttonBackgroundColor?: string;
  buttonSize?: string;
  buttonMargin?: string;
  iframeWidth?: string;
  iframeHeight?: string;
  displayType?: string;
  displayBehaviour?: string;
  modalBackdrop?: boolean;
  svgIcon?: string;
  customHeaders?: string;
  styleOverrides?: Record<string, string>;
  includeUrls?: string;
  excludeUrls?: string;
  aiProvider?: string;
  aiModel?: string;
  enableFeedback?: boolean;
  requiresEndUserAuth?: boolean;
  portalEnabled?: boolean;
  portalMembersEnabled?: boolean;
  portalKnowledgeBaseEnabled?: boolean;
  portalSkillsEnabled?: boolean;
  portalWorkflowsEnabled?: boolean;
  portalIntegrationsEnabled?: boolean;
  /** Show the Portal's Usage page — the AI allowance standing (WP-70). Default true. */
  portalUsageEnabled?: boolean;
  portalSettingsEnabled?: boolean;
  portalPromptsEnabled?: boolean;
  promptShortcutsEnabled?: boolean;
  serverIds?: string[]; // Multi-server support (1-5 servers)
  kbDocumentIds?: string[];
  kbWebSourceIds?: string[];
  kbFolderIds?: string[];
}

export const widgetService = {
  getAllWidgets: async (): Promise<WidgetConfig[]> => {
    const response = await apiClient.get("/widget");
    return response.data;
  },

  getWidgetById: async (id: string): Promise<WidgetConfig> => {
    const response = await apiClient.get(`/widget/${id}`);
    return response.data;
  },

  createWidget: async (data: CreateWidgetRequest): Promise<WidgetConfig> => {
    const response = await apiClient.post("/widget", data);
    return response.data;
  },

  updateWidget: async (
    id: string,
    data: Partial<WidgetConfig>,
  ): Promise<WidgetConfig> => {
    const response = await apiClient.put(`/widget/${id}`, data);
    return response.data;
  },

  deleteWidget: async (id: string): Promise<void> => {
    await apiClient.delete(`/widget/${id}`);
  },

  regenerateToken: async (id: string): Promise<WidgetConfig> => {
    const response = await apiClient.post(`/widget/${id}/regenerate-token`);
    return response.data;
  },
};
